import React, { useState } from 'react';
import {
  FiMail,
  FiLock,
  FiArrowLeft,
  FiCheckCircle,
  FiSend,
  FiShield,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';
import axios from 'axios';
import { getImagenUrl } from 'shared/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function RecuperarCliente({ schemaName, brandName, logoUrl, tokenInicial, onVolverLogin }) {
  const [paso, setPaso] = useState(tokenInicial ? 2 : 1);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(tokenInicial || '');
  const [claveNueva, setClaveNueva] = useState('');
  const [mostrarClave, setMostrarClave] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);

  const handleSolicitar = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Ingresa un correo electrónico válido');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/auth/recuperar-cliente`, {
        email: email.trim(),
        schema_name: schemaName,
      });
      setExito('Si el correo está registrado, recibirás un enlace de recuperación.');
      setPaso(2);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error de conexión. Intenta de nuevo.';
      setError(msg);
    } finally {
      setEnviando(false);
    }
  };

  const handleRestablecer = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Ingresa el código de recuperación');
      return;
    }
    if (!claveNueva || claveNueva.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setEnviando(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/auth/restablecer-cliente`, {
        token: token.trim(),
        clave_nueva: claveNueva,
        schema_name: schemaName,
      });
      setExito('Contraseña actualizada correctamente. Ya puedes iniciar sesión.');
      setPaso(3);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error de conexión. Intenta de nuevo.';
      setError(msg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={S.wrapper}>
      <div style={S.container}>
        <div style={S.card}>
          <img
            src={
              logoUrl
                ? getImagenUrl(logoUrl)
                : '/logo-agorax.jpeg'
            }
            alt={brandName}
            style={S.logoImg}
          />
          <h1 style={S.brand}>{brandName}</h1>

          {paso === 3 ? (
            <>
              <div style={S.iconSuccess}>
                <FiCheckCircle size={48} color="var(--color-success)" />
              </div>
              <p style={S.title}>Contrasena restablecida</p>
              <p style={S.subtitle}>{exito}</p>
              <button onClick={onVolverLogin} style={S.btnPrimary}>
                <FiArrowLeft size={16} />
                Volver al inicio de sesión
              </button>
            </>
          ) : paso === 1 ? (
            <>
              <div style={S.iconWrap}>
                <FiShield size={36} color="var(--color-secondary)" />
              </div>
              <p style={S.title}>Recuperar contraseña</p>
              <p style={S.subtitle}>
                Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu
                contraseña.
              </p>

              {error && <div style={S.errorBanner}>{error}</div>}

              <form onSubmit={handleSolicitar} style={S.form}>
                <div style={S.inputGroup}>
                  <FiMail size={17} color="var(--color-text-muted)" />
                  <input
                    type="email"
                    placeholder="Correo electronico"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={S.input}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={enviando}
                  style={{ ...S.btnPrimary, opacity: enviando ? 0.7 : 1 }}
                >
                  {enviando ? (
                    <>
                      <span style={S.spinner} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <FiSend size={16} />
                      Enviar enlace
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <div style={S.iconWrap}>
                <FiLock size={36} color="var(--color-secondary)" />
              </div>
              <p style={S.title}>Nueva contraseña</p>
              <p style={S.subtitle}>
                {exito ||
                  'Ingresa el código de recuperación que recibiste por correo y elige una nueva contraseña.'}
              </p>

              {error && <div style={S.errorBanner}>{error}</div>}

              <form onSubmit={handleRestablecer} style={S.form}>
                {!tokenInicial && (
                  <div style={S.inputGroup}>
                    <FiShield size={17} color="var(--color-text-muted)" />
                    <input
                      type="text"
                      placeholder="Codigo de recuperacion"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      style={S.input}
                      autoFocus
                    />
                  </div>
                )}
                <div style={S.inputGroup}>
                  <FiLock size={17} color="var(--color-text-muted)" />
                  <input
                    type={mostrarClave ? 'text' : 'password'}
                    placeholder="Nueva contraseña (min. 6 caracteres)"
                    value={claveNueva}
                    onChange={(e) => setClaveNueva(e.target.value)}
                    style={S.input}
                    autoComplete="new-password"
                    autoFocus={!!tokenInicial}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarClave(!mostrarClave)}
                    style={S.togglePwd}
                    tabIndex={-1}
                  >
                    {mostrarClave ? <FiEyeOff size={17} /> : <FiEye size={17} />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={enviando}
                  style={{ ...S.btnPrimary, opacity: enviando ? 0.7 : 1 }}
                >
                  {enviando ? (
                    <>
                      <span style={S.spinner} />
                      Restableciendo...
                    </>
                  ) : (
                    <>
                      <FiShield size={16} />
                      Restablecer contraseña
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {paso !== 3 && (
            <div style={S.links}>
              <button onClick={onVolverLogin} style={S.linkBtn}>
                <FiArrowLeft size={14} />
                Volver al inicio de sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-bg)',
    overflow: 'auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    minHeight: 0,
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '24px',
    padding: '36px 28px 28px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 32px rgba(11,26,74,0.08)',
    textAlign: 'center',
  },
  logoImg: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    objectFit: 'contain',
    marginBottom: '8px',
  },
  brand: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--color-primary)',
    margin: '0 0 8px',
    letterSpacing: '-0.3px',
  },
  iconWrap: {
    width: '64px',
    height: '64px',
    borderRadius: '20px',
    backgroundColor: '#eef1ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '8px auto',
  },
  iconSuccess: { margin: '16px auto 8px' },
  title: { fontSize: '18px', fontWeight: 700, color: 'var(--color-primary)', margin: '0 0 4px' },
  subtitle: {
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    margin: '0 0 20px',
    lineHeight: 1.5,
  },
  errorBanner: {
    fontSize: '13px',
    color: 'var(--color-danger)',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    padding: '10px 14px',
    borderRadius: '12px',
    marginBottom: '14px',
    textAlign: 'left',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  inputGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--color-bg)',
    borderRadius: '12px',
    padding: '12px 14px',
    border: '1px solid var(--color-border)',
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: 'var(--color-text)',
  },
  togglePwd: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--color-text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  btnPrimary: {
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '4px',
    fontFamily: 'inherit',
    letterSpacing: '0.1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'opacity 0.2s',
  },
  spinner: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'var(--color-surface)',
    animation: 'spin 0.6s linear infinite',
  },
  links: { marginTop: '14px' },
  linkBtn: {
    background: 'none',
    border: 'none',
    fontSize: '13px',
    color: 'var(--color-secondary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 600,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    margin: '0 auto',
  },
};

export default RecuperarCliente;
